import { EMPLOYEE_ROLE_CONFIG } from "@/constants/employees";
import { PERMISSION_GROUPS } from "@/constants/permissions";
import type { Employee } from "@/types";
import EmployeeStatusBadge from "./EmployeeStatusBadge";

interface EmployeeDetailsDrawerProps {
  open: boolean;
  employee: Employee | null;
  branchName: string;
  onClose: () => void;
  onEdit: (employee: Employee) => void;
  onEditPermissions: (employee: Employee) => void;
  onToggleStatus: (employee: Employee) => void;
}

export default function EmployeeDetailsDrawer({
  open,
  employee,
  branchName,
  onClose,
  onEdit,
  onEditPermissions,
  onToggleStatus,
}: EmployeeDetailsDrawerProps) {
  if (!open || !employee) return null;

  const role = EMPLOYEE_ROLE_CONFIG[employee.role];
  const assignedGroups = PERMISSION_GROUPS.map((group) => ({
    ...group,
    permissions: group.permissions.filter((permission) =>
      employee.permissions.includes(permission.key),
    ),
  })).filter((group) => group.permissions.length > 0);

  const infoRows = [
    { label: "رقم الجوال", value: employee.phone, dir: "ltr" as const },
    { label: "الفرع", value: branchName },
    { label: "الدور", value: role.label },
    { label: "تاريخ إضافة الموظف", value: employee.createdAt },
    { label: "آخر تسجيل دخول", value: employee.lastLogin },
  ];

  return (
    <div className="fixed inset-0 z-[70]">
      <div
        className="absolute inset-0 bg-foreground-950/50 yc-fade-in"
        onClick={onClose}
        aria-hidden="true"
      ></div>

      <div className="absolute inset-y-0 end-0 w-full max-w-lg bg-white yc-drawer-left flex flex-col shadow-pop">
        <div className="shrink-0 flex items-start justify-between gap-3 px-5 py-4 border-b border-background-200">
          <h3 className="text-lg font-bold text-foreground-950">
            تفاصيل الموظف
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="w-9 h-9 shrink-0 flex items-center justify-center rounded-full text-foreground-500 hover:bg-background-100 transition-colors duration-200 cursor-pointer"
            aria-label="إغلاق"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-5">
          <div className="flex items-center gap-4">
            <span className="w-16 h-16 shrink-0 flex items-center justify-center rounded-full bg-primary-100 text-primary-700 text-2xl font-bold">
              {employee.name.charAt(0)}
            </span>
            <div className="min-w-0">
              <h4 className="text-lg font-bold text-foreground-950 truncate">
                {employee.name}
              </h4>
              <div className="mt-1.5 flex flex-wrap items-center gap-2">
                <span
                  className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold whitespace-nowrap ${role.className}`}
                >
                  <i className={role.icon}></i>
                  {role.label}
                </span>
                <EmployeeStatusBadge status={employee.status} />
              </div>
              <p className="mt-2 text-xs leading-5 text-foreground-500">
                {role.description}
              </p>
            </div>
          </div>

          <dl className="mt-5 rounded-box border border-background-200 divide-y divide-background-100">
            {infoRows.map((row) => (
              <div
                key={row.label}
                className="flex items-center justify-between gap-3 px-4 py-3"
              >
                <dt className="text-sm text-foreground-500">{row.label}</dt>
                <dd
                  className="text-sm font-medium text-foreground-900"
                  dir={row.dir}
                >
                  {row.value}
                </dd>
              </div>
            ))}
          </dl>

          <div className="mt-6">
            <div className="flex items-center justify-between gap-2 mb-3">
              <h4 className="text-sm font-bold text-foreground-950">
                الصلاحيات الممنوحة
              </h4>
              <span className="rounded-full bg-background-100 px-2.5 py-0.5 text-xs font-medium text-foreground-600">
                {employee.permissions.length} صلاحية
              </span>
            </div>

            {assignedGroups.length === 0 ? (
              <p className="rounded-box border border-dashed border-background-300 px-4 py-6 text-center text-sm text-foreground-500">
                لا توجد صلاحيات ممنوحة لهذا الموظف.
              </p>
            ) : (
              <div className="space-y-3">
                {assignedGroups.map((group) => (
                  <div
                    key={group.key}
                    className="rounded-box border border-background-200 overflow-hidden"
                  >
                    <div className="flex items-center gap-2.5 px-4 py-2.5 bg-background-50 border-b border-background-100">
                      <span
                        className={`w-7 h-7 flex items-center justify-center rounded-full ${group.tone}`}
                      >
                        <i className={group.icon}></i>
                      </span>
                      <span className="text-sm font-semibold text-foreground-900">
                        {group.label}
                      </span>
                    </div>
                    <ul className="px-4 py-2">
                      {group.permissions.map((permission) => (
                        <li
                          key={permission.key}
                          className="flex items-center gap-2 py-1.5 text-sm text-foreground-700"
                        >
                          <i className="ri-checkbox-circle-fill text-primary-500"></i>
                          <span className="flex-1">{permission.label}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="shrink-0 border-t border-background-200 px-5 py-4">
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => onEdit(employee)}
              className="inline-flex items-center justify-center gap-2 h-11 rounded-btn bg-background-100 text-foreground-700 text-sm font-semibold hover:bg-background-200 transition-colors duration-200 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-edit-line"></i>
              تعديل البيانات
            </button>
            <button
              type="button"
              onClick={() => onEditPermissions(employee)}
              className="inline-flex items-center justify-center gap-2 h-11 rounded-btn bg-accent-100 text-accent-900 text-sm font-semibold hover:bg-accent-200 transition-colors duration-200 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-shield-check-line"></i>
              تعديل الصلاحيات
            </button>
          </div>
          <button
            type="button"
            onClick={() => onToggleStatus(employee)}
            className={`mt-3 w-full inline-flex items-center justify-center gap-2 h-11 rounded-btn text-sm font-semibold transition-colors duration-200 cursor-pointer whitespace-nowrap ${
              employee.status === "active"
                ? "bg-red-50 text-red-600 hover:bg-red-100"
                : "bg-primary-500 text-white hover:bg-primary-600"
            }`}
          >
            <i
              className={
                employee.status === "active"
                  ? "ri-pause-circle-line"
                  : "ri-play-circle-line"
              }
            ></i>
            {employee.status === "active"
              ? "إيقاف الحساب"
              : "إعادة تفعيل الحساب"}
          </button>
        </div>
      </div>
    </div>
  );
}